import { api } from "@bindery/convex-reader/api";
import type { ConvexReactClient } from "convex/react";
import type { FunctionArgs, FunctionReturnType } from "convex/server";

export type SubmitArgs = Omit<
  FunctionArgs<typeof api.learning.submit>,
  "expectedReaderId"
>;
export type SubmitResult = FunctionReturnType<typeof api.learning.submit>;
export type WireBookmark = FunctionArgs<
  typeof api.library.upsertBookmarks
>["bookmarks"][number];
export type UpsertBookmarksResult = FunctionReturnType<
  typeof api.library.upsertBookmarks
>;
export type ChangedSincePage = Exclude<
  FunctionReturnType<typeof api.fluency.changedSince>,
  null | ReaderMismatch
>;
export type FluencyHistories = Exclude<
  FunctionReturnType<typeof api.fluency.forWords>,
  null | ReaderMismatch
>;

interface ReaderMismatch {
  readerMismatch: true;
}

export const READER_MISMATCH = "reader_mismatch";

/** `null` when signed out; `READER_MISMATCH` when the token is another reader's. */
export type ReaderAnswer<T> = T | null | typeof READER_MISMATCH;

// No normalized word has an uppercase letter, so a forWords answer can never
// carry this key as a word.
function isReaderMismatch(value: unknown): value is ReaderMismatch {
  return (
    typeof value === "object" &&
    value !== null &&
    "readerMismatch" in value &&
    value.readerMismatch === true
  );
}

const toReaderAnswer = <T>(
  value: T | ReaderMismatch | null,
): ReaderAnswer<T> => (isReaderMismatch(value) ? READER_MISMATCH : value);

/**
 * The four convex-reader calls the local store makes; faked in tests. Each
 * names the reader its rows belong to: the server refuses a token for any
 * other reader instead of filing the rows under it.
 */
export interface SyncBackend {
  submit: (readerId: string, args: SubmitArgs) => Promise<SubmitResult>;
  upsertBookmarks: (
    readerId: string,
    bookmarks: WireBookmark[],
  ) => Promise<UpsertBookmarksResult>;
  changedSince: (
    readerId: string,
    since: number,
    cursor: string | null,
    numItems: number,
  ) => Promise<ReaderAnswer<ChangedSincePage>>;
  forWords: (
    readerId: string,
    words: string[],
  ) => Promise<ReaderAnswer<FluencyHistories>>;
}

// One-shot client.query, not a subscription: a subscription to
// changedSince would re-run after every page turn's submit.
export function convexSyncBackend(
  client: Pick<ConvexReactClient, "mutation" | "query">,
): SyncBackend {
  return {
    submit: (expectedReaderId, args) =>
      client.mutation(api.learning.submit, { expectedReaderId, ...args }),
    upsertBookmarks: (expectedReaderId, bookmarks) =>
      client.mutation(api.library.upsertBookmarks, {
        expectedReaderId,
        bookmarks,
      }),
    changedSince: async (expectedReaderId, since, cursor, numItems) =>
      toReaderAnswer(
        await client.query(api.fluency.changedSince, {
          expectedReaderId,
          since,
          paginationOpts: { cursor, numItems },
        }),
      ),
    forWords: async (expectedReaderId, words) =>
      toReaderAnswer(
        await client.query(api.fluency.forWords, { expectedReaderId, words }),
      ),
  };
}

/** The `kind` of a convex-reader `ConvexError`, read structurally. */
export function convexErrorKind(error: unknown): string | null {
  const data = errorData(error);
  return typeof data?.kind === "string" ? data.kind : null;
}

export function convexErrorReason(error: unknown): string | null {
  const data = errorData(error);
  return typeof data?.reason === "string" ? data.reason : null;
}

function errorData(error: unknown): Record<string, unknown> | null {
  if (typeof error !== "object" || error === null || !("data" in error)) {
    return null;
  }
  const { data } = error;
  return typeof data === "object" && data !== null
    ? (data as Record<string, unknown>)
    : null;
}
